/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 */

import { types, applySnapshot, getSnapshot } from 'mobx-state-tree';
import Framework from '@greenville/framework';
import utils from '../../../../common/utils';
import * as constants from '../../constants/AgenticServiceConstants';
import { PROMPT_MANAGEMENT_LANG_KEYS } from '../../constants/AgenticPromptMgmtConstants';
import { BERLIN_API_TIMEOUT } from '../../../studio/constants/ServiceConstants';
import { mapResolvedDocument, mergeResolvedDocument } from '../../../studio/utils/promptApiMappers';
import { normalizeCatalogueRows, normalizeCataloguePagination } from '../../../../common/prompt/catalogueUtils';
import { normalizeHistoryRows } from '../../utils/promptManagementUtils';

const normalizeVersionRows = (responseData) => {
  if (!responseData) return [];
  const rows = Array.isArray(responseData) ? responseData : responseData.data ?? responseData.versions ?? [];
  return Array.isArray(rows)
    ? rows.map((v) => mapResolvedDocument({ ...v, id: v.id || v.promptRef })).filter(Boolean)
    : [];
};

const PromptMessageModel = types.model('AgenticPromptMessageModel', {
  role: types.maybeNull(types.string),
  content: types.maybeNull(types.string)
});

const ResolvedPromptModel = types.model('AgenticResolvedPromptModel', {
  id: types.maybeNull(types.string),
  promptRef: types.maybeNull(types.string),
  version: types.maybeNull(types.string),
  promptVersionRef: types.maybeNull(types.string),
  status: types.maybeNull(types.string),
  name: types.maybeNull(types.string),
  comments: types.maybeNull(types.string),
  description: types.maybeNull(types.string),
  isAgentic: types.optional(types.boolean, false),
  previousVersion: types.maybeNull(types.string),
  promptStart: types.optional(types.array(PromptMessageModel), []),
  promptEnd: types.optional(types.array(PromptMessageModel), []),
  createdDate: types.maybeNull(types.number),
  updatedDate: types.maybeNull(types.string),
  modifiedBy: types.maybeNull(types.string)
});

const CatalogueEntryModel = types.model('AgenticCatalogueEntryModel', {
  promptRef: types.maybeNull(types.string),
  promptVersionRef: types.maybeNull(types.string),
  name: types.maybeNull(types.string),
  description: types.maybeNull(types.string),
  comments: types.maybeNull(types.string),
  modifiedBy: types.maybeNull(types.string),
  updatedDate: types.maybeNull(types.string),
  createdDate: types.maybeNull(types.string),
  versionCount: types.maybeNull(types.number),
  promptType: types.maybeNull(types.string)
});

const HistoryEntryModel = types.model('AgenticHistoryEntryModel', {
  action_type: types.maybeNull(types.string),
  edited_by: types.maybeNull(types.string),
  date_edited: types.maybeNull(types.string),
  previousVersion: types.maybeNull(types.string),
  promptVersionRef: types.maybeNull(types.string),
  snapshot: types.maybeNull(types.frozen()),
  previousSnapshot: types.maybeNull(types.frozen())
});

const AssociatedGraphModel = types.model('AgenticAssociatedGraphModel', {
  graphId: types.maybeNull(types.string),
  graphName: types.maybeNull(types.string),
  version: types.maybeNull(types.number),
  lifeCycleStatus: types.maybeNull(types.string)
});

const AgenticPromptModel = types
  .model('AgenticPromptModel', {
    activeResolved: types.maybeNull(ResolvedPromptModel),
    versions: types.optional(types.array(ResolvedPromptModel), []),
    catalogue: types.optional(types.array(CatalogueEntryModel), []),
    cataloguePagination: types.maybeNull(types.frozen()),
    history: types.optional(types.array(HistoryEntryModel), []),
    associatedGraphs: types.optional(types.array(AssociatedGraphModel), []),
    associatedGraphsMeta: types.maybeNull(types.frozen()),
    lastError: types.maybeNull(types.string),
    lastMutation: types.maybeNull(types.frozen()),
    lastCreated: types.maybeNull(types.frozen())
  })
  .actions((self) => {
    const publish = (event, payload) => Framework.getEventManager().publish(event, payload);

    const notifyError = ({ message = '', langMessage = '' }) => {
      Framework.getStoreRegistry().getStore('notificationState').set({
        open: true,
        type: 'error',
        message,
        langMessage
      });
    };

    const fail = (lastError, errorEvent, notification = {}) => {
      applySnapshot(self, { ...getSnapshot(self), lastError });
      notifyError(notification);
      publish(errorEvent);
    };

    return {
      clearLastError() {
        applySnapshot(self, { ...getSnapshot(self), lastError: null });
      },

      setVersions(rows) {
        applySnapshot(self, { ...getSnapshot(self), lastError: null, versions: rows || [] });
      },

      setCatalogue(rows) {
        applySnapshot(self, { ...getSnapshot(self), lastError: null, catalogue: rows || [] });
      },

      setCataloguePagination(pagination) {
        applySnapshot(self, { ...getSnapshot(self), cataloguePagination: pagination || null });
      },

      setHistory(rows) {
        applySnapshot(self, { ...getSnapshot(self), lastError: null, history: rows || [] });
      },

      setActiveResolved(data) {
        if (!data) {
          applySnapshot(self, { ...getSnapshot(self), activeResolved: null });
          return;
        }
        applySnapshot(self, {
          ...getSnapshot(self),
          lastError: null,
          activeResolved: mapResolvedDocument(data)
        });
      },

      resetPanelState() {
        applySnapshot(self, {
          activeResolved: null,
          versions: [],
          catalogue: [],
          cataloguePagination: null,
          history: [],
          associatedGraphs: [],
          associatedGraphsMeta: null,
          lastError: null,
          lastMutation: null,
          lastCreated: null
        });
      },

      fetchCatalogue(payload) {
        self.clearLastError();
        publish(constants.GET_PROMPT_CATALOGUE, { payload });
      },

      fetchVersions(payload) {
        self.clearLastError();
        publish(constants.GET_AGENTIC_PROMPT_VERSIONS, { payload });
      },

      fetchVersion(payload) {
        self.clearLastError();
        publish(constants.GET_AGENTIC_PROMPT_VERSION, { payload });
      },

      fetchHistory(payload) {
        self.clearLastError();
        publish(constants.GET_AGENTIC_PROMPT_HISTORY, { payload });
      },

      createPrompt(payload) {
        self.clearLastError();
        publish(constants.CREATE_AGENTIC_PROMPT, { payload });
      },

      checkNameAvailability(payload) {
        self.clearLastError();
        publish(constants.GET_PROMPT_NAME_AVAILABILITY, { payload });
      },

      fetchAssociatedGraphs(payload) {
        self.clearLastError();
        applySnapshot(self, {
          ...getSnapshot(self),
          associatedGraphs: [],
          associatedGraphsMeta: null
        });
        publish(constants.GET_PROMPT_VERSION_GRAPHS, { payload });
      },

      updatePrompt(payload) {
        self.clearLastError();
        publish(constants.UPDATE_AGENTIC_PROMPT, { payload });
      },

      handleServiceError(error, fallbackLangKey, errorEvent) {
        const { response } = error;
        let lastError = fallbackLangKey;
        let notification = { langMessage: fallbackLangKey };

        if (response) {
          const { status, data } = response;
          if (status === 401) {
            utils.redirectToLogin();
            publish(errorEvent);
            return;
          }
          const serverMessage = data?.error || data?.message;
          if (serverMessage) {
            lastError = serverMessage;
            notification = { message: serverMessage };
          }
        } else if (error.message === `timeout of ${BERLIN_API_TIMEOUT}ms exceeded`) {
          lastError = PROMPT_MANAGEMENT_LANG_KEYS.REQUEST_TIMEOUT;
          notification = { langMessage: PROMPT_MANAGEMENT_LANG_KEYS.REQUEST_TIMEOUT };
        }

        applySnapshot(self, { ...getSnapshot(self), lastError });
        notifyError(notification);
        publish(errorEvent);
      },

      handleCatalogueResponse(data, { search = '', dataContext = {} } = {}) {
        if (data && data.success !== false) {
          const mapped = normalizeCatalogueRows(data, dataContext);
          const pagination = normalizeCataloguePagination(data);
          self.setCatalogue(mapped);
          self.setCataloguePagination(pagination);
          publish(constants.GET_PROMPT_CATALOGUE_SUCCESS, { data: mapped, pagination, search });
          return;
        }
        const serverMessage = data?.error || data?.message;
        if (serverMessage) {
          fail(serverMessage, constants.GET_PROMPT_CATALOGUE_ERROR, { message: serverMessage });
          return;
        }
        fail(PROMPT_MANAGEMENT_LANG_KEYS.ERROR_CATALOGUE, constants.GET_PROMPT_CATALOGUE_ERROR, {
          langMessage: PROMPT_MANAGEMENT_LANG_KEYS.ERROR_CATALOGUE
        });
      },

      handleHistoryResponse(data, { promptRef = null } = {}) {
        if (data && data.success !== false) {
          const rows = normalizeHistoryRows(data);
          self.setHistory(rows);
          publish(constants.GET_AGENTIC_PROMPT_HISTORY_SUCCESS, { data: rows, promptRef });
          return;
        }
        const serverMessage = data?.error || data?.message;
        if (serverMessage) {
          fail(serverMessage, constants.GET_AGENTIC_PROMPT_HISTORY_ERROR, { message: serverMessage });
          return;
        }
        fail(PROMPT_MANAGEMENT_LANG_KEYS.ERROR_HISTORY, constants.GET_AGENTIC_PROMPT_HISTORY_ERROR, {
          langMessage: PROMPT_MANAGEMENT_LANG_KEYS.ERROR_HISTORY
        });
      },

      handleNameAvailabilityResponse(data) {
        if (data && typeof data.available === 'boolean') {
          applySnapshot(self, { ...getSnapshot(self), lastError: null });
          publish(constants.GET_PROMPT_NAME_AVAILABILITY_SUCCESS, { data });
          return;
        }
        const serverMessage = data?.error || data?.message;
        if (serverMessage) {
          fail(serverMessage, constants.GET_PROMPT_NAME_AVAILABILITY_ERROR, { message: serverMessage });
          return;
        }
        fail(PROMPT_MANAGEMENT_LANG_KEYS.ERROR_NAME_AVAILABILITY, constants.GET_PROMPT_NAME_AVAILABILITY_ERROR, {
          langMessage: PROMPT_MANAGEMENT_LANG_KEYS.ERROR_NAME_AVAILABILITY
        });
      },

      handleAssociatedGraphsResponse(data, { promptRef = null, promptVersionRef = null } = {}) {
        if (data && data.success !== false && Array.isArray(data.graphs)) {
          const graphs = data.graphs.map((item) => {
            let lifeCycleStatus = null;
            if (item?.lifeCycleStatus != null) {
              lifeCycleStatus = String(item.lifeCycleStatus);
            } else if (item?.lifecycleStatus != null) {
              lifeCycleStatus = String(item.lifecycleStatus);
            }
            const rawName = item?.name != null ? String(item.name).trim() : '';
            return {
              graphId: item?.graphId != null ? String(item.graphId) : null,
              graphName: rawName || null,
              version: typeof item?.version === 'number' ? item.version : Number(item?.version) || null,
              lifeCycleStatus
            };
          });
          const meta = {
            promptRef: data.promptRef || promptRef,
            promptVersionRef: data.promptVersionRef || promptVersionRef,
            total: typeof data.total === 'number' ? data.total : graphs.length
          };
          const graphsForUi = graphs.map((graph) => ({ ...graph }));
          applySnapshot(self, {
            ...getSnapshot(self),
            lastError: null,
            associatedGraphs: graphs,
            associatedGraphsMeta: meta
          });
          publish(constants.GET_PROMPT_VERSION_GRAPHS_SUCCESS, { data: graphsForUi, meta });
          return;
        }
        const serverMessage = data?.error || data?.message;
        if (serverMessage) {
          fail(serverMessage, constants.GET_PROMPT_VERSION_GRAPHS_ERROR, { message: serverMessage });
          return;
        }
        fail(PROMPT_MANAGEMENT_LANG_KEYS.ERROR_ASSOCIATED_GRAPHS, constants.GET_PROMPT_VERSION_GRAPHS_ERROR, {
          langMessage: PROMPT_MANAGEMENT_LANG_KEYS.ERROR_ASSOCIATED_GRAPHS
        });
      },

      handleCreateResponse(data) {
        if (data && data.success !== false) {
          const mapped = mergeResolvedDocument(null, {
            ...data,
            id: data.id || data.promptRef,
            isAgentic: data.isAgentic === true || data.isAgentic === false ? data.isAgentic : true
          });
          const mutation = {
            promptRef: data.promptRef || data.id,
            promptVersionRef: data.promptVersionRef || data.version || '1.0.0',
            version: data.version || '1.0.0'
          };
          applySnapshot(self, {
            ...getSnapshot(self),
            lastError: null,
            lastMutation: mutation,
            lastCreated: mapped || data,
            activeResolved: mapped
          });
          publish(constants.CREATE_AGENTIC_PROMPT_SUCCESS, { data: mapped || data });
          return;
        }
        const serverMessage = data?.error || data?.message;
        if (serverMessage) {
          fail(serverMessage, constants.CREATE_AGENTIC_PROMPT_ERROR, { message: serverMessage });
          return;
        }
        fail(PROMPT_MANAGEMENT_LANG_KEYS.ERROR_CREATE, constants.CREATE_AGENTIC_PROMPT_ERROR, {
          langMessage: PROMPT_MANAGEMENT_LANG_KEYS.ERROR_CREATE
        });
      },

      handleVersionsResponse(data, { promptRef = null } = {}) {
        if (data && data.success !== false) {
          const mapped = normalizeVersionRows(data);
          self.setVersions(mapped);
          publish(constants.GET_AGENTIC_PROMPT_VERSIONS_SUCCESS, { data: mapped, promptRef });
          return;
        }
        const serverMessage = data?.error || data?.message;
        if (serverMessage) {
          fail(serverMessage, constants.GET_AGENTIC_PROMPT_VERSIONS_ERROR, { message: serverMessage });
          return;
        }
        fail(PROMPT_MANAGEMENT_LANG_KEYS.ERROR_VERSIONS, constants.GET_AGENTIC_PROMPT_VERSIONS_ERROR, {
          langMessage: PROMPT_MANAGEMENT_LANG_KEYS.ERROR_VERSIONS
        });
      },

      handleVersionResponse(data, { promptRef = null, promptVersionRef = null } = {}) {
        if (data && data.success !== false) {
          const mapped = mapResolvedDocument({ ...data, id: data.id || data.promptRef || promptRef });
          if (mapped) {
            self.setActiveResolved(mapped);
          }
          publish(constants.GET_AGENTIC_PROMPT_VERSION_SUCCESS, {
            data: mapped || data,
            promptRef,
            promptVersionRef
          });
          return;
        }
        const serverMessage = data?.error || data?.message;
        if (serverMessage) {
          fail(serverMessage, constants.GET_AGENTIC_PROMPT_VERSION_ERROR, { message: serverMessage });
          return;
        }
        fail(PROMPT_MANAGEMENT_LANG_KEYS.ERROR_VERSION, constants.GET_AGENTIC_PROMPT_VERSION_ERROR, {
          langMessage: PROMPT_MANAGEMENT_LANG_KEYS.ERROR_VERSION
        });
      },

      handleUpdateResponse(data, { promptRef = null } = {}) {
        if (data && data.success !== false) {
          const mutation = {
            promptRef: data.promptRef || data.id || promptRef,
            promptVersionRef: data.promptVersionRef || data.version,
            version: data.version,
            previousVersion: data.previousVersion
          };
          const existingSnapshot = self.activeResolved ? getSnapshot(self.activeResolved) : null;
          const mapped = mergeResolvedDocument(existingSnapshot, { ...data, id: mutation.promptRef });
          applySnapshot(self, {
            ...getSnapshot(self),
            lastError: null,
            lastMutation: mutation,
            activeResolved: mapped
          });
          publish(constants.UPDATE_AGENTIC_PROMPT_SUCCESS, { data: mutation, document: mapped });
          return;
        }
        const serverMessage = data?.error || data?.message;
        if (serverMessage) {
          fail(serverMessage, constants.UPDATE_AGENTIC_PROMPT_ERROR, { message: serverMessage });
          return;
        }
        fail(PROMPT_MANAGEMENT_LANG_KEYS.ERROR_SAVE, constants.UPDATE_AGENTIC_PROMPT_ERROR, {
          langMessage: PROMPT_MANAGEMENT_LANG_KEYS.ERROR_SAVE
        });
      }
    };
  });

export default AgenticPromptModel;
